// R7 — parse an HTTP error Response into a typed HttpError carrying a WireError.
//
// The backend HTTP error helper (services/server/error_envelope.go::httpErrorJSON
// and internal/recordings/http_handler.go::httpErr) writes a snake_case JSON
// body: { error: <safe message>, error_code: "ERROR_CODE_*", retryable: bool,
// field?: string, feature?: string, required_tier?: string, limit?: number,
// limit_unit?: string, current?: number }. This module turns a non-OK Response into an HttpError whose
// `.message` is the warm/safe catalog string and whose `.wire` carries the typed
// code — so callers can `toastStore.showError(e.wire)`.

import { friendlyMessage, isLimitUnitName, limitValues, type WireError } from '@/lib/errors/catalog';
import { ErrorCode } from '@/lib/protobuf/gen/errors/errors_pb';

function codeFromName(name: unknown): ErrorCode | undefined {
  if (typeof name !== 'string') return undefined;
  const key = name.replace(/^ERROR_CODE_/, '');
  const val = (ErrorCode as Record<string, unknown>)[key];
  return typeof val === 'number' ? (val as ErrorCode) : undefined;
}

/**
 * HttpError is the typed rejection thrown for a non-OK HTTP response. `.message`
 * is the safe catalog message (never the raw body); `.wire` carries the typed
 * code + retryable hint; `.status` is the HTTP status for callers that branch on
 * it (e.g. 401 → re-auth).
 */
export class HttpError extends Error {
  readonly wire: WireError;
  readonly status: number;
  constructor(wire: WireError, status: number) {
    super(friendlyMessage(wire.code, wire.message, limitValues(wire)));
    this.name = 'HttpError';
    this.wire = wire;
    this.status = status;
  }
}

/**
 * httpWireError reads a non-OK Response body and builds an HttpError. It accepts
 * the typed snake_case JSON shape, and degrades gracefully to a status-derived
 * generic message for plain-text / opaque bodies (older endpoints not yet
 * migrated through the mapper). Never throws; never surfaces raw body prose
 * beyond what the safe JSON `error` field carried.
 */
export async function httpWireError(res: Response): Promise<HttpError> {
  let raw: unknown;
  try {
    const text = await res.text();
    if (text) {
      try {
        raw = JSON.parse(text) as unknown;
      } catch {
        // Non-JSON body (legacy endpoint). Don't surface its prose —
        // wireFromBody falls back to a status-derived generic message.
      }
    }
  } catch {
    // Body read failed — status-derived fallback below.
  }
  return new HttpError(wireFromBody(raw, res.status), res.status);
}

/**
 * wireFromBody builds a WireError from an ALREADY-PARSED error body (or
 * undefined for non-JSON/empty bodies). Split out of httpWireError so
 * callers that must read the Response body themselves (apiFetch reads it
 * once for both this and its legacy `code` field) share the exact same
 * normalization: typed snake_case shape when present, status-derived generic
 * fallback when neither code nor message could be extracted.
 */
export function wireFromBody(raw: unknown, status: number): WireError {
  let wire: WireError = {};
  if (typeof raw === 'object' && raw !== null) {
    const body = raw as {
      error?: unknown;
      error_code?: unknown;
      code?: unknown;
      retryable?: unknown;
      field?: unknown;
      feature?: unknown;
      required_tier?: unknown;
      limit?: unknown;
      limit_unit?: unknown;
      current?: unknown;
    };
    // Build conditionally — exactOptionalPropertyTypes forbids assigning an
    // explicit `undefined` to an optional field.
    const parsed: WireError = {};
    const code = codeFromName(body.error_code);
    if (code !== undefined) parsed.code = code;
    // Raw machine code, preserved even when the ErrorCode enum has no member
    // for it. Handlers that answer with a bare snake_case code (the /demo gate
    // sends `{"error":"demo_read_only", …}`) put it in `error`, so that field
    // doubles as a code source — `parsed.message` keeps it too, and
    // isDemoReadOnlyRefusal reads either.
    const rawName =
      typeof body.error_code === 'string'
        ? body.error_code
        : typeof body.code === 'string'
          ? body.code
          : undefined;
    if (rawName) parsed.codeName = rawName;
    // Only trust `error` as a fallback message — it's the backend's safe
    // string. We do NOT echo arbitrary plain-text bodies.
    if (typeof body.error === 'string') parsed.message = body.error;
    if (typeof body.retryable === 'boolean') parsed.retryable = body.retryable;
    // BILL-20a (241): the refusal's subject. Kept only when non-empty so a
    // toast id is never keyed on "".
    if (typeof body.feature === 'string' && body.feature) parsed.feature = body.feature;
    if (typeof body.required_tier === 'string' && body.required_tier) {
      parsed.requiredTier = body.required_tier;
    }
    // LIMIT-03: the refusal's numbers (httperr.body). 0 or absent ⇒ not set.
    if (typeof body.limit === 'number' && body.limit > 0) parsed.limit = body.limit;
    if (isLimitUnitName(body.limit_unit)) parsed.limitUnit = body.limit_unit;
    if (typeof body.current === 'number' && body.current > 0) parsed.current = body.current;
    wire = parsed;
  }

  // If we couldn't derive a code or a safe message, synthesize one from status
  // so the user still sees a warm message rather than "HTTP 500".
  if (wire.code == null && !wire.message) {
    wire = { ...wire, ...wireFromStatus(status) };
  }
  return wire;
}

function wireFromStatus(status: number): WireError {
  switch (status) {
    case 401:
      return { code: ErrorCode.UNAUTHORIZED };
    case 403:
      return { code: ErrorCode.FORBIDDEN };
    case 404:
      return { code: ErrorCode.NOT_FOUND };
    case 409:
      return { code: ErrorCode.CONFLICT };
    case 413:
      return { code: ErrorCode.PAYLOAD_TOO_LARGE };
    case 415:
      return { code: ErrorCode.UNSUPPORTED_MEDIA };
    case 423:
      return { code: ErrorCode.RECORDING_SESSION_LOCKED };
    case 429:
      return { code: ErrorCode.RATE_LIMITED };
    case 503:
      return { code: ErrorCode.UNAVAILABLE };
    case 504:
      return { code: ErrorCode.TIMEOUT };
    default:
      return { code: ErrorCode.INTERNAL };
  }
}
